/** The realtime speech-to-speech services Arc Voice can run on. */
export enum ArcVoiceProvider {
  Xai = 'xai',
}

/**
 * The one tool the voice model gets, which runs the request on Arc's full agent.
 * Named for what it does rather than "ask Arc": the name shapes how the voice talks about it.
 */
export const ARC_VOICE_TOOL_NAME = 'handle_request'

/** The voice's own way to move the screen: run in the browser, with no Arc turn behind it. */
export const ARC_VOICE_GO_TO_TOOL_NAME = 'go_to'

/** Set an agenda item aside to come back to later in the call. Run in the browser. */
export const ARC_VOICE_PARK_TOOL_NAME = 'park_item'

/** Record an objective or a way of working for the rest of the call. Run in the browser. */
export const ARC_VOICE_BRIEF_TOOL_NAME = 'set_call_brief'

/** Stop requests from this call that are still running. Run in the browser, with no Arc turn. */
export const ARC_VOICE_CANCEL_TOOL_NAME = 'cancel_request'

/**
 * What the caller is on. On a phone the call holds the whole screen, so Arc never
 * moves it: opening a page there takes them off the call.
 */
export enum ArcVoiceDevice {
  Desktop = 'desktop',
  Mobile = 'mobile',
}

/** xAI's built-in voices. Their docs name these but describe none of them; listen and pick. */
export enum ArcVoiceName {
  Ara = 'ara',
  Eve = 'eve',
  Leo = 'leo',
  Rex = 'rex',
  Sal = 'sal',
}

export const ARC_VOICE_NAME_LABELS: Record<ArcVoiceName, string> = {
  [ArcVoiceName.Ara]: 'Ara — warm',
  [ArcVoiceName.Eve]: 'Eve — clear, the default',
  [ArcVoiceName.Leo]: 'Leo — confident, direct',
  [ArcVoiceName.Rex]: 'Rex — energetic',
  [ArcVoiceName.Sal]: 'Sal — distinctive',
}

/** How Arc should sound. Steered through its instructions; the provider has no accent switch. */
export enum ArcVoiceAccent {
  Natural = 'natural',
  American = 'american',
  British = 'british',
  Australian = 'australian',
}

export const ARC_VOICE_ACCENT_LABELS: Record<ArcVoiceAccent, string> = {
  [ArcVoiceAccent.Natural]: "The voice's own",
  [ArcVoiceAccent.American]: 'American',
  [ArcVoiceAccent.British]: 'British',
  [ArcVoiceAccent.Australian]: 'Australian',
}

/** How readily talking over Arc cuts it off. */
export enum ArcVoiceCutInSensitivity {
  /** A loud room, or speakers: only plainly raised speech stops Arc. */
  Low = 'low',
  Normal = 'normal',
  /** A headset or a quiet room: speaking at all stops Arc. */
  High = 'high',
}

export const ARC_VOICE_CUT_IN_LABELS: Record<ArcVoiceCutInSensitivity, string> = {
  [ArcVoiceCutInSensitivity.Low]: 'Low — only when you speak up',
  [ArcVoiceCutInSensitivity.Normal]: 'Normal',
  [ArcVoiceCutInSensitivity.High]: 'High — as soon as you speak',
}

/** The mic level (0–1) that counts as cutting in at each sensitivity, while Arc is talking. */
export const ARC_VOICE_CUT_IN_LEVELS: Record<ArcVoiceCutInSensitivity, number> = {
  [ArcVoiceCutInSensitivity.Low]: 0.45,
  [ArcVoiceCutInSensitivity.Normal]: 0.3,
  [ArcVoiceCutInSensitivity.High]: 0.18,
}

/** PCM16 mono, both directions. */
export const ARC_VOICE_SAMPLE_RATE = 24_000

/** Silence on both sides for this long pauses the call, so nobody is billed for an open mic. */
export const ARC_VOICE_IDLE_TIMEOUT_MS = 60 * 1000

/** The same while their mic is muted: room for a side conversation, but never an open line for good. */
export const ARC_VOICE_MUTED_IDLE_TIMEOUT_MS = 5 * 60 * 1000

/** In a huddle, someone answering Arc this soon after it asked them something is talking to it. */
export const ARC_VOICE_ROOM_FOLLOW_UP_MS = 8 * 1000

/** Hard ceiling on one connection; the call pauses there and resumes on a fresh one. */
export const ARC_VOICE_MAX_SESSION_MS = 20 * 60 * 1000

/** How long the provider keeps a paused conversation for a resume to pick up. */
export const ARC_VOICE_RESUME_WINDOW_MS = 30 * 60 * 1000

/** How often a browser holding a call checkpoints it, paused or not, to show it is still there. */
export const ARC_VOICE_HEARTBEAT_MS = 60 * 1000

/** A live call row with no checkpoint for this long was abandoned, and the server ends it. */
export const ARC_VOICE_CALL_STALE_MS = 5 * 60 * 1000

/** Longest one answer poll is held open before it returns Working. */
export const ARC_VOICE_ANSWER_MAX_WAIT_MS = 15 * 1000

/** Bounds on the transcript a call record keeps. */
export const ARC_VOICE_TRANSCRIPT_MAX_LINES = 400
export const ARC_VOICE_TRANSCRIPT_LINE_MAX_CHARS = 4000

export interface IArcVoiceTool {
  name: string
  description: string
  parameters: Record<string, unknown>
}

/**
 * Everything the browser needs to open a call: where to connect, the short-lived
 * credential to connect with, and the session it configures once connected.
 */
export interface IArcVoiceSession {
  provider: ArcVoiceProvider
  url: string
  protocols: string[]
  expiresAt: string
  voice: string
  /** Talking speed, 1 being the voice's natural pace. */
  speechSpeed: number
  /** Speech-to-text model for live captions of the caller. */
  transcriptionModel: string
  instructions: string
  /** The page the caller is on, in words, when the call opened. */
  pageContext: string | null
  /** The mic level (0–1) that counts as cutting Arc off: the caller's sensitivity setting. */
  cutInLevel: number
  tools: IArcVoiceTool[]
  sampleRate: number
  idleTimeoutMs: number
  mutedIdleTimeoutMs: number
  maxSessionMs: number
  /** Arc is in a huddle: it answers only when addressed, so the voice never replies on its own. */
  isRoom?: boolean
}

export enum ArcVoiceAnswerStatus {
  /** Arc finished; `spokenAnswer` is what to say. */
  Answered = 'answered',
  /** Arc finished but is waiting on an approval card in the DM. */
  AwaitingApproval = 'awaiting_approval',
  /** Still running; ask again for the answer. */
  Working = 'working',
  Failed = 'failed',
}

export interface IArcVoiceAnswer {
  status: ArcVoiceAnswerStatus
  spokenAnswer: string | null
  /** A draft Arc wrote for them (an email, a message, a note), to be read out in full. */
  draft: string | null
  /** The draft ran past what can be read aloud, so `draft` is only its opening. */
  isDraftCut: boolean
  /** The turn answering the request — what the answer poll asks about. Null when none started. */
  chatHistoryId: string | null
  /** The Arc session the turn runs in, which stopping it is keyed on. Sent when a request starts. */
  sessionId?: string | null
  /** Arc's live status line ("Running query..."), while it works. */
  progress: string | null
}

export enum ArcVoiceSpeaker {
  User = 'user',
  Arc = 'arc',
}

export interface IArcVoiceCallLine {
  speaker: ArcVoiceSpeaker
  text: string
  /** ISO timestamp the line was spoken. */
  at: string
}

export enum ArcVoiceCallRecordStatus {
  /** Held open in a browser, live or paused, which checkpoints it as it runs. */
  Live = 'live',
  Ended = 'ended',
}

/**
 * The call as the DM keeps it: written when the call connects and checkpointed
 * as it runs, so a refresh or a closed tab never loses what was said.
 */
export interface IArcVoiceCallRecord {
  /** Absent on rows written before calls were checkpointed, which were all finished. */
  status?: ArcVoiceCallRecordStatus
  startedAt: string
  endedAt: string | null
  /** Time actually on the call; pauses excluded. */
  durationMs: number
  transcript: IArcVoiceCallLine[]
  /** Where everything asked on the call stood when it ended; the recap is built from it. */
  agenda?: IArcVoiceCallAgendaEntry[]
  /** How loud Arc's echo and the caller's cut-ins were, for tuning the cut-in level. */
  audioStats?: IArcVoiceAudioStats
}

/** What the mic heard while Arc was talking, over a whole call. Levels are 0–1. */
export interface IArcVoiceAudioStats {
  /** The cut-in level the call ran with. */
  cutInLevel: number
  /** Chunks heard while Arc talked that didn't cut it off: mostly its own echo. */
  echoChunks: number
  echoP95: number
  echoPeak: number
  /** Times the caller cut Arc off, and how loud they were when they did. */
  cutIns: number
  cutInMin: number | null
  cutInMedian: number | null
}

/** How an item on a call's agenda ended up, as the call record keeps it. */
export enum ArcVoiceCallItemState {
  Done = 'done',
  NeedsApproval = 'needs_approval',
  Failed = 'failed',
  Running = 'running',
  Parked = 'parked',
  /** Cancelled on the call before it finished. */
  Stopped = 'stopped',
}

export interface IArcVoiceCallAgendaEntry {
  number: number
  title: string
  state: ArcVoiceCallItemState
  /** What Arc found or did, in a line, when it has said. */
  result: string | null
}

/** Bounds on the agenda a call record keeps. */
export const ARC_VOICE_AGENDA_MAX_ITEMS = 100
export const ARC_VOICE_AGENDA_TEXT_MAX_CHARS = 1000

/** The fields a checkpoint sends. */
export type IArcVoiceCallCheckpoint = Pick<IArcVoiceCallRecord, 'durationMs'> & {
  status: ArcVoiceCallRecordStatus
  endedAt?: string
  /** Absent keeps the transcript already stored: an unload beacon can't carry a long one. */
  transcript?: IArcVoiceCallLine[]
  /** Where the agenda stands, so a call the server ends still gets its recap. */
  agenda?: IArcVoiceCallAgendaEntry[]
  /** Sent as the call ends. */
  audioStats?: IArcVoiceAudioStats
}
